import React from "react";

import { Dot } from "lucide-react";

import { LegalPageLayout } from "~/components/legal/legal-page-layout";
import { H2, H3, H4, P, A, UL, LI } from "~/components/ui/typography";
import { cn } from "~/lib/utils";
import { PRODUCT_NAME } from "~/lib/env.shared";
import { SUPPORT_EMAIL, SERVER_URL } from "~/lib/env.shared";

import { useIsMobile } from "../hooks/use-mobile";

type TableOfContentsItem = {
    id: string;
    title: string;
    children?: TableOfContentsItem[];
};

export default function Terms() {
    const isMobile = useIsMobile();
    const tableOfContents = [
        { id: "introduction", title: "1. Introduction" },
        { id: "description-of-service", title: "2. Description of Service" },
        {
            id: "account-registration-and-eligibility",
            title: "3. Account Registration and Eligibility",
            children: [
                { id: "registration", title: "3.1 Registration" },
                { id: "eligibility", title: "3.2 Eligibility" },
                { id: "account-security", title: "3.3 Account Security" },
            ],
        },
        {
            id: "user-responsibilities-and-prohibited-uses",
            title: "4. User Responsibilities and Prohibited Uses",
            children: [
                { id: "permitted-uses", title: "4.1 Permitted Uses" },
                { id: "prohibited-uses", title: "4.2 Prohibited Uses" },
            ],
        },
        {
            id: "privacy-and-data-usage",
            title: "5. Privacy and Data Usage",
            children: [
                { id: "privacy-policy", title: "5.1 Privacy Policy" },
                {
                    id: "session-donation-program",
                    title: "5.2 Session Donation Program",
                },
                { id: "hipaa-compliance", title: "5.3 HIPAA Compliance" },
            ],
        },
        {
            id: "intellectual-property-rights",
            title: "6. Intellectual Property Rights",
            children: [
                {
                    id: "ownership-of-service",
                    title: "6.1 Ownership of Service",
                },
                {
                    id: "ownership-of-content",
                    title: "6.2 Ownership of Content",
                },
                {
                    id: "analysis-and-reports",
                    title: "6.3 Analysis and Reports",
                },
            ],
        },
        {
            id: "termination",
            title: "7. Termination",
            children: [
                { id: "termination-by-you", title: "7.1 Termination by You" },
                { id: "termination-by-us", title: "7.2 Termination by Us" },
                {
                    id: "effect-of-termination",
                    title: "7.3 Effect of Termination",
                },
            ],
        },
        {
            id: "disclaimers-and-limitations-of-liability",
            title: "8. Disclaimers and Limitations of Liability",
            children: [
                {
                    id: "service-provided-as-is",
                    title: '8.1 Service Provided "As Is"',
                },
                {
                    id: "no-therapeutic-or-medical-advice",
                    title: "8.2 No Therapeutic or Medical Advice",
                },
                {
                    id: "no-guarantee-of-availability",
                    title: "8.3 No Guarantee of Availability",
                },
                {
                    id: "limitation-of-liability",
                    title: "8.4 Limitation of Liability",
                },
            ],
        },
        { id: "indemnification", title: "9. Indemnification" },
        { id: "changes-to-terms", title: "10. Changes to Terms" },
        { id: "governing-law", title: "11. Governing Law" },
        { id: "contact-information", title: "12. Contact Information" },
    ];

    const renderTableOfContents = (items: TableOfContentsItem[], level = 0) => (
        <UL
            className={cn(
                "space-y-1",
                level === 0 ? "text-sm" : "text-sm mt-1",
                level > 0 ? "pl-4" : "",
            )}
        >
            {items.map((item: TableOfContentsItem) => (
                <LI key={item.id} className="relative">
                    <A
                        href={`#${item.id}`}
                        className="block py-1 text-zinc-500 transition-colors duration-200 hover:text-rose-600"
                    >
                        {item.title}
                    </A>
                    {item.children &&
                        renderTableOfContents(item.children, level + 1)}
                </LI>
            ))}
        </UL>
    );

    return (
        <LegalPageLayout
            pageTitle="Terms of Service"
            lastUpdated="April 2, 2025"
            tableOfContents={tableOfContents}
        >
            <section id="introduction">
                <H2>1. Introduction</H2>
                <P>
                    Welcome to {PRODUCT_NAME} ("Service"), a product developed
                    by Evolve Labs, Inc. ("we," "our," "us"). {PRODUCT_NAME}
                    provides Internal Family Systems (IFS) clients and
                    practitioners with the ability to record, transcribe, and
                    analyze therapy sessions.
                </P>
                <P>
                    By accessing or using our Service, you agree to be bound by
                    these Terms of Service ("Terms"). Please read these Terms
                    carefully. If you do not agree to these Terms, please do not
                    use the Service.
                </P>
            </section>

            <section id="description-of-service">
                <H2>2. Description of Service</H2>
                <P>
                    Our Service is designed to provide additional insight into
                    IFS themes via custom generative algorithmic analysis of
                    therapy sessions by:
                </P>
                <UL className="text-md mb-4 list-disc space-y-1 pl-5 text-gray-800">
                    <LI>
                        Direct upload of recorded therapy sessions or by
                        invitation of our recorder to voice or video meeting
                        events on platforms like Zoom or Google Meet,
                    </LI>
                    <LI>
                        Transcription of therapy sessions provided to the
                        Service
                    </LI>
                    <LI>
                        AI-powered analysis and insights derived from
                        transcripts, and
                    </LI>
                    <LI>
                        Ongoing storage and management of session recordings and
                        transcripts.
                    </LI>
                </UL>
            </section>

            <section id="account-registration-and-eligibility">
                <H2>3. Account Registration and Eligibility</H2>

                <section id="registration">
                    <H3>3.1 Registration</H3>
                    <P>
                        To use the Service, you may need to create an account by
                        providing information about yourself. You agree that all
                        information you provide is accurate, complete, and
                        current at all times.
                    </P>
                </section>

                <section id="eligibility">
                    <H3>3.2 Eligibility</H3>
                    <P>
                        The Service is intended for people who are at least 18
                        years of age. If you are under 18 years of age, you may
                        only use the Service with the explicit consent and
                        oversight of a parent or guardian, or as authorized by
                        your healthcare provider or practitioner. By using the
                        Service, you represent and warrant that you meet these
                        eligibility requirements.
                    </P>
                </section>

                <section id="account-security">
                    <H3>3.3 Account Security</H3>
                    <P>
                        You are responsible for maintaining the confidentiality
                        of your account credentials and for all activities that
                        occur under your account. You agree to notify us
                        immediately of any unauthorized use of your account or
                        any other breach of security.
                    </P>
                </section>
            </section>

            <section id="user-responsibilities-and-prohibited-uses">
                <H2>4. User Responsibilities and Use</H2>

                <section id="permitted-uses">
                    <H3>4.1 Permitted Uses</H3>
                    <P>
                        You may use the Service solely for your personal,
                        non-commercial therapeutic purposes or, in the case of
                        practitioners, for providing therapy services to your
                        clients.
                    </P>
                </section>

                <section id="prohibited-uses">
                    <H3>4.2 Prohibited Uses</H3>
                    <P>You agree you will not use the Service to:</P>
                    <UL className="text-md mb-4 list-disc space-y-1 pl-5 text-gray-800">
                        <LI>Violate any applicable laws or regulations</LI>
                        <LI>
                            Infringe on the intellectual property rights of
                            Evolve Labs or any third party,
                        </LI>
                        <LI>
                            Upload, transmit, or share content that is unlawful,
                            harmful, threatening, abusive, harassing,
                            defamatory, vulgar, obscene, or otherwise
                            objectionable,
                        </LI>
                        <LI>Impersonate any person or entity,</LI>
                        <LI>
                            Upload the same session multiple times or otherwise
                            attempt to misuse system resources,
                        </LI>
                        <LI>
                            Engage in any activity that interferes with or
                            disrupts the Service,
                        </LI>
                        <LI>
                            Attempt to gain unauthorized access to the Service
                            or related systems,
                        </LI>
                        <LI>
                            Use data or content obtained through the Service to
                            train machine learning models or AI systems,
                        </LI>
                        <LI>
                            Redistribute, sell, or commercially exploit any
                            content, analysis, or reports generated by the
                            Service,
                        </LI>
                        <LI>
                            Reverse engineer, decompile, or disassemble any part
                            of the Service,
                        </LI>
                        <LI>
                            Attempt to bypass any measure we may use to prevent
                            or restrict access to the Service.
                        </LI>
                    </UL>
                </section>
            </section>

            <section id="privacy-and-data-usage">
                <H2>5. Privacy and Data Usage</H2>

                <section id="privacy-policy">
                    <H3>5.1 Privacy Policy</H3>
                    <P>
                        Your use of the Service is also governed by our Privacy
                        Policy, which is incorporated by reference into these
                        Terms of Service. Please review our{" "}
                        <A
                            href="/privacy"
                            className="text-rose-600 hover:underline"
                        >
                            Privacy Policy
                        </A>{" "}
                        to understand our practices.
                    </P>
                </section>

                <section id="session-donation-program">
                    <H3>5.2 Session Donation Program</H3>
                    <P>
                        You may opt to donate your sessions to Evolve Labs for
                        research and development purposes. If you choose to
                        participate in this program, you authorize us to access
                        your transcripts in full to understand feedback and
                        improve our technology and algorithmic models. You may
                        opt in or out of this program at any time through your
                        account settings.
                    </P>
                </section>

                <section id="hipaa-compliance">
                    <H3>5.3 HIPAA Compliance</H3>
                    <P>
                        For practitioners subject to HIPAA requirements, we
                        require the execution of a Business Associate Agreement
                        (BAA) accessible via{" "}
                        <A
                            href="/baa"
                            className="text-rose-600 hover:underline"
                        >
                            {`${SERVER_URL}/onboarding/practitioner/baa`}
                        </A>
                        . Review and sign the BAA found here and email to{" "}
                        <A
                            href={`mailto:${SUPPORT_EMAIL}`}
                            className="text-rose-600 hover:underline"
                        >
                            {SUPPORT_EMAIL}
                        </A>
                        . It is considered in effect once all parties have
                        signed the agreement. Practitioners are responsible for
                        ensuring they have obtained appropriate consent from
                        their clients before using the Service.
                    </P>
                </section>
            </section>

            <section id="intellectual-property-rights">
                <H2>6. Intellectual Property Rights</H2>

                <section id="ownership-of-service">
                    <H3>6.1 Ownership of Service</H3>
                    <P>
                        The Service, including all of its components, features,
                        and functionality, is owned by Evolve Labs and is
                        protected by copyright, trademark, and other
                        intellectual property laws. These Terms do not grant you
                        any right, title, or interest in the Service.
                    </P>
                </section>

                <section id="ownership-of-content">
                    <H3>6.2 Ownership of Content</H3>
                    <P>
                        You retain ownership of the content you upload to the
                        Service, including recordings and transcripts. However,
                        by using the Service, you grant us a non-exclusive,
                        worldwide, royalty-free license to use, reproduce,
                        process, adapt, and publish your content solely for the
                        purpose of providing and improving the Service.
                    </P>
                </section>

                <section id="analysis-and-reports">
                    <H3>6.3 Analysis and Reports</H3>
                    <P>
                        The analysis and reports generated by the Service are
                        provided for informational purposes only. While we
                        employ sophisticated algorithmic and automated
                        technologies to analyze session transcriptions and
                        create these reports, you acknowledge that they are
                        derivative works based on your content and our
                        proprietary analytical methods. You may use these
                        reports for personal or professional purposes but may
                        not claim ownership of our analytical methods or
                        technology. Furthermore, you acknowledge the use of
                        generative technologies may contain errors or omissions
                        and hold harmless both employees of, and entities
                        providing the Service, which includes but is not limited
                        to Evolve Labs, {PRODUCT_NAME}, and its business
                        associates in the event of incorrect or incomplete
                        transcription analysis.
                    </P>
                </section>
            </section>

            <section id="termination">
                <H2>7. Termination</H2>

                <section id="termination-by-you">
                    <H3>7.1 Termination by You</H3>
                    <P>
                        You may terminate your account at any time through the
                        account settings page. Upon termination, all of your
                        content will be deleted from our system, except as may
                        be required by law or as necessary to protect our legal
                        interests.
                    </P>
                </section>

                <section id="termination-by-us">
                    <H3>7.2 Termination by Us</H3>
                    <P>
                        We reserve the right to suspend or terminate your access
                        to the Service at any time for any reason, including but
                        not limited to a violation of these Terms. Upon
                        termination, your right to use the Service will
                        immediately cease.
                    </P>
                </section>

                <section id="effect-of-termination">
                    <H3>7.3 Effect of Termination</H3>
                    <P>
                        Upon termination of your account, we will delete all of
                        your content within a reasonable time, except as may be
                        required by law or as necessary to protect our legal
                        interests.
                    </P>
                </section>
            </section>

            <section id="disclaimers-and-limitations-of-liability">
                <H2>8. Disclaimers and Limitations of Liability</H2>

                <section id="service-provided-as-is">
                    <H3>8.1 Service Provided "As Is"</H3>
                    <P>
                        THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE"
                        BASIS, WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR
                        IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF
                        MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
                        NON-INFRINGEMENT, OR COURSE OF PERFORMANCE.
                    </P>
                </section>

                <section id="no-therapeutic-or-medical-advice">
                    <H3>8.2 No Therapeutic or Medical Advice</H3>
                    <P>
                        The Service and Platform is not a healthcare provider or
                        a substitute for professional therapy or medical advice.
                        The analysis and reports generated by the Service are
                        intended to supplement, not replace, the relationship
                        between you and your healthcare provider.
                    </P>
                </section>

                <section id="no-guarantee-of-availability">
                    <H3>8.3 No Guarantee of Availability</H3>
                    <P>
                        We do not guarantee that the Service will be
                        uninterrupted, timely, secure, or error-free. You
                        understand that we may experience delays, interruptions,
                        and limitations due to maintenance, upgrades, or factors
                        beyond our control.
                    </P>
                </section>

                <section id="limitation-of-liability">
                    <H3>8.4 Limitation of Liability</H3>
                    <P>
                        TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, IN NO
                        EVENT SHALL EVOLVE LABS BE LIABLE FOR ANY INDIRECT,
                        PUNITIVE, INCIDENTAL, SPECIAL, CONSEQUENTIAL DAMAGES, OR
                        ANY DAMAGES WHATSOEVER INCLUDING, WITHOUT LIMITATION,
                        DAMAGES FOR LOSS OF USE, DATA, OR PROFITS, ARISING OUT
                        OF OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THE
                        SERVICE, WITH THE DELAY OR INABILITY TO USE THE SERVICE
                        OR RELATED SERVICES, THE PROVISION OF OR FAILURE TO
                        PROVIDE SERVICES, OR FOR ANY INFORMATION, SOFTWARE,
                        PRODUCTS, SERVICES AND RELATED GRAPHICS OBTAINED THROUGH
                        THE SERVICE, WHETHER BASED ON CONTRACT, TORT,
                        NEGLIGENCE, STRICT LIABILITY OR OTHERWISE, EVEN IF
                        EVOLVE LABS HAS BEEN ADVISED OF THE POSSIBILITY OF
                        DAMAGES.
                    </P>
                </section>
            </section>

            <section id="indemnification">
                <H2>9. Indemnification</H2>
                <P>
                    You agree to defend, indemnify, and hold harmless Evolve
                    Labs, its officers, directors, employees, and agents, from
                    and against any and all claims, damages, obligations,
                    losses, liabilities, costs or debt, and expenses (including
                    but not limited to attorney's fees) arising from: (i) your
                    use of and access to the Service; (ii) your violation of any
                    term of these Terms; (iii) your violation of any third party
                    right, including without limitation any copyright, property,
                    or privacy right; or (iv) any claim that your content caused
                    damage to a third party.
                </P>
            </section>

            <section id="changes-to-terms">
                <H2>10. Changes to Terms</H2>
                <P>
                    We reserve the right to modify these Terms at any time. If
                    we make changes to these Terms, we will provide notice of
                    such changes, such as by sending an email notification,
                    providing notice through the Service, or updating the "Last
                    Updated" date at the beginning of these Terms. Your
                    continued use of the Service following the posting of
                    revised Terms means that you accept and agree to the
                    changes.
                </P>
            </section>

            <section id="governing-law">
                <H2>11. Governing Law</H2>
                <P>
                    These Terms shall be governed and construed in accordance
                    with the laws of the State of Colorado, without regard to
                    its conflict of law provisions.
                </P>
            </section>

            <section id="contact-information">
                <H2>12. Contact Information</H2>
                <P>
                    If you have any questions about these Terms, please contact
                    us at{" "}
                    <A
                        href={`mailto:${SUPPORT_EMAIL}`}
                        className="text-rose-600 hover:underline"
                    >
                        {SUPPORT_EMAIL}
                    </A>{" "}
                    or at our mailing address:
                </P>
                <P>
                    Evolve Labs Inc.
                    <br />
                    601 16th St., STE-C175
                    <br />
                    Golden, CO 80401
                </P>
            </section>
        </LegalPageLayout>
    );
}
